import { useCallback, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { ARTICLE_PICTURE_CAPTION_ATTRIBUTE, articleHeadings, articleSegments, fill, sanitizeArticleBody, sanitizeArticleView, t } from '@bms2/shared';
import { RichTextView } from '../../components/controls/RichTextEditor';
import { labelCallouts } from '../../components/controls/richTextCallout';
import { articlePictureImagePath } from './api';
import { PictureViewer, type ViewedPicture } from './PictureViewer';
import styles from './knowledge.module.css';

/**
 * An article's words as a reader sees them (piece 30, Article Editor). 🔨 The
 * table of contents is computed here, at render time, from the article's own
 * H1 and H2 nodes, and the stored body carries only the empty marker. The
 * same split feeds the PDF, so nothing is converted with loss at export.
 */
export function ArticleBody({ html, fileHref, pictureHref }: { html: string; fileHref: (fileId: string) => string; pictureHref: (fileId: string) => string }) {
  const container = useRef<HTMLDivElement>(null);
  // 🔒 Every render runs the article's own allowed table, then draws each picture from the API's own address for it (decision 0800).
  const sanitize = useCallback((segment: string) => zoomable(sanitizeArticleView(segment, pictureHref)), [pictureHref]);
  const [viewed, setViewed] = useState<ViewedPicture | null>(null);
  const closeViewer = useCallback(() => setViewed(null), []);
  const pictureAt = (target: EventTarget): HTMLImageElement | null =>
    target instanceof HTMLImageElement && target.hasAttribute('data-kb-zoom') && target.closest('[data-rich-text-view="article"]') ? target : null;
  const openPicture = (picture: HTMLImageElement) =>
    setViewed({ src: picture.currentSrc || picture.src, alt: picture.getAttribute('alt') ?? '', caption: picture.getAttribute(ARTICLE_PICTURE_CAPTION_ATTRIBUTE) ?? '' });
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const picture = pictureAt(event.target);
    if (picture) openPicture(picture);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const picture = pictureAt(event.target);
    if (!picture || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    openPicture(picture);
  };
  // 🔨 The split, the headings and the drawn segments are worked out once per article, so opening a picture does not redo them (T652).
  const segments = useMemo(() => articleSegments(html, fileHref), [html, fileHref]);
  const headings = useMemo(() => articleHeadings(sanitizeArticleBody(html)), [html]);
  const drawn = useMemo(() => {
    const jump = (index: number) => {
      const target = container.current?.querySelectorAll('[data-rich-text-view="article"] h1, [data-rich-text-view="article"] h2')[index];
      if (target instanceof HTMLElement) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    return segments.map((segment, index) =>
        segment.kind === 'toc' ? (
          <nav key={`toc-${index}`} className={styles.toc} data-article-toc="true" aria-label={t('kb.toc.title')}>
            <p className={styles.tocTitle}>{t('kb.toc.title')}</p>
            {headings.length === 0 ? (
              <p className={styles.hint}>{t('kb.toc.empty')}</p>
            ) : (
              <ol className={styles.tocList}>
                {headings.map((heading, headingIndex) => (
                  <li key={`${heading.text}-${headingIndex}`} className={heading.level === 2 ? styles.tocLevel2 : undefined} data-toc-level={heading.level}>
                    <button type="button" className={styles.tocLink} onClick={() => jump(headingIndex)}>{heading.text}</button>
                  </li>
                ))}
              </ol>
            )}
          </nav>
        ) : (
          <RichTextView key={`words-${index}`} html={segment.html} sanitize={sanitize} className={styles.readingView} data-rich-text-view="article" data-reading-view="true" />
        ),
    );
  }, [segments, headings, sanitize]);
  return (
    <div ref={container} className={styles.body} data-article-body="true" onClick={onClick} onKeyDown={onKeyDown}>
      {drawn}
      {viewed ? <PictureViewer picture={viewed} onClose={closeViewer} /> : null}
    </div>
  );
}

/**
 * 🔒 A reader's picture opens at full size on a press (T735; decision 1476), so
 * each one takes the focus and is marked for that press. A picture inside a link
 * stays the link's: it takes no second stop and opens nothing. 🔒 A screen
 * reader hears it as a button naming that action and the alt text, where one
 * exists (the KB4 review), not as a bare picture that happens to take focus.
 */
function zoomable(html: string): string {
  const holder = document.createElement('template');
  holder.innerHTML = html;
  for (const picture of holder.content.querySelectorAll('img')) {
    if (picture.closest('a')) continue;
    const alt = (picture.getAttribute('alt') ?? '').trim();
    picture.setAttribute('tabindex', '0');
    picture.setAttribute('role', 'button');
    picture.setAttribute('aria-label', alt ? fill(t('kb.picture.openNamed'), { alt }) : t('kb.picture.open'));
    picture.setAttribute('data-kb-zoom', 'true');
  }
  // 🔒 A callout is named for a screen reader by the word the stylesheet draws (decision 10530; the T3572 review).
  labelCallouts(holder.content);
  return holder.innerHTML;
}

/** The absolute address of a File's bytes, which the allowlist keeps as a link where a relative one is dropped. */
export function fileStreamHref(apiBaseUrl: string): (fileId: string) => string {
  return (fileId) => {
    const relative = `${apiBaseUrl}/files/${fileId}/stream`;
    return typeof window === 'undefined' ? relative : new URL(relative, window.location.href).href;
  };
}

/**
 * 🔒 Where a picture in an article's words is drawn from (decision 0800): the
 * API's own route for that picture of that article, which answers whoever may
 * read a version drawing it. 🔨 Never an address from the internet.
 */
export function articlePictureHref(apiBaseUrl: string, articleId: string): (fileId: string) => string {
  return (fileId) => {
    const relative = `${apiBaseUrl}${articlePictureImagePath(articleId, fileId)}`;
    return typeof window === 'undefined' ? relative : new URL(relative, window.location.href).href;
  };
}
